import {Injectable} from '@angular/core';
import {environment} from '@environments/environment';
import {BehaviorSubject, concatMap, forkJoin, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';

export interface IS3LoadProgress {
  isLoading: boolean;
  filename: string;
  fileSize: number;
  steps: number;
  currentStep: number;
}

const defaultS3LoadProgress: IS3LoadProgress = {
  isLoading: false,
  filename: '',
  fileSize: 0,
  steps: 0,
  currentStep: 0
};

@Injectable({
  providedIn: 'root'
})
export class LoadS3FileService {

  public s3LoadProgress$: BehaviorSubject<IS3LoadProgress> = new BehaviorSubject<IS3LoadProgress>(defaultS3LoadProgress);

  constructor(
    private http: HttpClient,
  ) {
  }

  public loadFileFromS3(url: string, fileSize = 0) {

    this.s3LoadProgress$.next({
      ...defaultS3LoadProgress,
      isLoading: true,
      filename: url,
      fileSize
    });

    // url = url.replace(environment.dashboardDatasBase, environment.dashboardDatasBaseCF);
    // const forceRefreshTag = `?forceRefresh=${new Date().getTime()}`;
    // url = `${url}${noForceCacheNeeded ? '' : forceRefreshTag}`;
    url = this.addForceRefreshParam(url);

    if (!fileSize) {
      // On va demander à s3 la taille qu'il fait ?
      // return this.http.get<any>(url, {});
      return this.getFileSize(url).pipe(
        concatMap(size => {
          return this.doLoadSegments(url, size);
        })
      );
    } else {
      return this.doLoadSegments(url, fileSize);
    }

  }

  private doLoadSegments(url: string, fileSize: number) {

    const segmentSize = 5 * 1024 * 1024; // 5 Mo en octets
    const totalSize = fileSize;
    const segments = [];

    for (let start = 0; start < totalSize; start += segmentSize) {
      const end = Math.min(start + segmentSize - 1, totalSize - 1);
      const headers = {
        headers: {
          Range: `bytes=${start}-${end}`
        }
      };

      segments.push(this.http.get(url, {
        ...headers,
        responseType: 'text'
      }).pipe(
        concatMap(segment => {
          // Augmenter la valeur de currentStep
          this.s3LoadProgress$.next({
            ...this.s3LoadProgress$.value,
            currentStep: this.s3LoadProgress$.value.currentStep + 1
          });
          return of(segment);
        })
      ));

    }

    this.s3LoadProgress$.next({
      ...this.s3LoadProgress$.value,
      steps: segments.length,
      currentStep: 0
    });

    return forkJoin(segments)
      .pipe(
        map(segmentArray => {
          const jsonString = segmentArray.join('');
          const jsonData = JSON.parse(jsonString);

          this.s3LoadProgress$.next({
            ...this.s3LoadProgress$.value,
            isLoading: false
          });
          return jsonData;
        })
      );

  }

  private getFileSize(url: string) {
    return this.http.head(url, {observe: 'response'}).pipe(
      map(response => {
        const contentLength = response.headers.get('Content-Length');
        if (contentLength) {
          return Number(contentLength);
        } else {
          throw new Error(`Content-Length header is not available for ${url}`);
        }
      })
    );
  }

  private addForceRefreshParam(url: string): string {
    // Remplace dashboardDatasBase par dashboardDatasBaseCF dans l'URL
    // url = url.replace(environment.dashboardDatasBase, environment.dashboardDatasBaseCF);

    // Extract existing URL parameters
    const urlObject = new URL(url);
    const params = new URLSearchParams(urlObject.search);

    // Create forceRefresh parameter
    params.set('forceRefresh', new Date().getTime().toString());
    // Construct the new URL with updated parameters
    urlObject.search = params.toString();

    return urlObject.toString();
  }

}
